import React, { Suspense, lazy } from 'react';
import { Route, Switch, BrowserRouter, Redirect } from 'react-router-dom';
import { Provider } from 'react-redux';
import './App.css';
import './views/ShoppingDiagnostics/SDForm/SDLegs/SDLegs.css';
import AuthComponent from './auth/AuthComponent';
import BaseLayout from './layout/BaseLayout';
import applicationStore from './store/ApplicationStore';
import notificationStore from './store/NotificationStore';
import NotificationsManager from './views/NotificationsManager/NotificationsManager';
import LoadingBlock from './components/LoadingBlock/LoadingBlock';
import PrivateRouter from './components/PrivateRouter/PrivateRouter';
import Login from './views/Pages/Login/Login';
import { DEFAULT_PATHS } from './utils/ApiUtils';
import { CATEGORIES } from './utils/PermissionsChecker';
import { ThemeProvider } from './layout/ThemeProvider';
import { ThemeConfigKeys } from './utils/Constants';
import BookingTolerances from './views/BookingTolerances/BookingTolerances';

const Home = lazy(() => import('./views/Pages/Home/Home'));
const Page404 = lazy(() => import('./views/Pages/Page404/Page404'));
const Page403 = lazy(() => import('./views/Pages/Page403/Page403'));
const SearchesManager = lazy(() => import('./views/SearchesManager/SearchesManager'));
const SearchManagerRule = lazy(() => import('./views/SearchesManager/SearchManagerRule/SearchManagerRule'));
const SearchesManagerV2 = lazy(() => import('./views/SearchesManagerV2/SearchesManagerV2'));
const SearchesManagerV2Editor = lazy(() => import('./views/SearchesManagerV2/SearchesManagerV2Editor'));
const VccCredentialsEditor = lazy(() => import('./views/VccCredentials/VccCredentialsEditor'));
const WexCredentialsEditor = lazy(() => import('./views/VccCredentials/WexCredentialsEditor'));
const BookingCreateManagerV2 = lazy(() => import('./views/BookingCreateManagerV2/BookingCreateManagerV2'));
const BookingCreateManagerV2Editor = lazy(() => import('./views/BookingCreateManagerV2/BookingCreateManagerV2Editor'));
const BookingCreateManagerV2RemarksEditor = lazy(() => import('./views/BookingCreateManagerV2/BookingCreateManagerV2RemarksEditor'));
const BookingCreateManagerV2SSREditor = lazy(() => import('./views/BookingCreateManagerV2/BookingCreateManagerV2SSREditor'));
const BookingTolerancesEditor = lazy(() => import('./views/BookingTolerances/BookingTolerancesEditor'));
const Diagnostics = lazy(() => import('./views/ShoppingDiagnostics/Diagnostics'));
const BookingCreateManager = lazy(() => import('./views/BCM/OSIS/BookingCreateManagerOSIS'));
const BCMRule = lazy(() => import('./views/BCM/OSIS/BCMRuleOSIS/BCMRuleOSIS'));
const BCMRemarksRule = lazy(() => import('./views/BCM/Remarks/BCMRemarksRule/BCMRemarksRule'));
const BookingCreateManagerRemarks = lazy(() => import('./views/BCM/Remarks/BookingCreateManagerRemarks'));
const BookingCreateManagerSSR = lazy(() => import('./views/BCM/SSR/BookingCreateManagerSSR'));
const BookingCreateManagerSSREditor = lazy(() => import('./views/BCM/SSR/BookingCreateManagerSSREditor'));
const Regions = lazy(() => import('./views/BusinessData/Regions/Regions'));
const BusinessData = lazy(() => import('./views/BusinessData/BusinessData'));
const RegionsEditor = lazy(() => import('./views/BusinessData/Regions/RegionsEditor/RegionsEditor'));
const Countries = lazy(() => import('./views/BusinessData/Countries/Countries'));
const CountriesEditor = lazy(() => import('./views/BusinessData/Countries/CountriesEditor/CountriesEditor'));
const Locations = lazy(() => import('./views/BusinessData/Locations/Locations'));
const LocationsEditor = lazy(() => import('./views/BusinessData/Locations/LocationsEditor/LocationsEditor'));
const Stations = lazy(() => import('./views/BusinessData/Stations/Stations'));
const StationsEditor = lazy(() => import('./views/BusinessData/Stations/StationsEditor/StationsEditor'));
const Airlines = lazy(() => import('./views/BusinessData/Airlines/Airlines'));
const AirlinesEditor = lazy(() => import('./views/BusinessData/Airlines/AirlinesEditor/AirlinesEditor'));
const Brands = lazy(() => import('./views/BusinessData/Brands/Brands'));
const BrandsEditor = lazy(() => import('./views/BusinessData/Brands/BrandsEditor/BrandsEditor'));
const Markets = lazy(() => import('./views/BusinessData/Markets/Markets'));
const MarketsEditor = lazy(() => import('./views/BusinessData/Markets/MarketsEditor/MarketsEditor'));
const DistributionChannels = lazy(() => import('./views/BusinessData/DistributionChannels/DistributionChannels'));
const DistributionChannelsEditor = lazy(() => import('./views/BusinessData/DistributionChannels/DistributionChannelsEditor/DistributionChannelsEditor'));
const Agencies = lazy(() => import('./views/BusinessData/Agencies/Agencies'));
const AgenciesEditor = lazy(() => import('./views/BusinessData/Agencies/AgenciesEditor/AgenciesEditor'));
const AgencyGroups = lazy(() => import('./views/BusinessData/AgencyGroups/AgencyGroups'));
const AgencyGroupsEditor = lazy(() => import('./views/BusinessData/AgencyGroups/AgencyGroupsEditor/AgencyGroupsEditor'));
const Markups = lazy(() => import('./views/Markups/Markups'));
const MarkupsV2 = lazy(() => import('./views/MarkupsV2/MarkupsV2'));
const MarkupsEditor = lazy(() => import('./views/Markups/MarkupsEditor/MarkupsEditor'));
const MarkupsV2Editor = lazy(() => import('./views/MarkupsV2/MarkupsV2Editor'));
const ProviderPreference = lazy(() => import('./views/ProviderPreference/ProviderPreference'));
const ProviderPreferenceEditor = lazy(() => import('./views/ProviderPreference/ProviderPreferenceEditor'));
const ProviderCredentials = lazy(() => import('./views/ProviderCredentials/ProviderCredentials'));
const ProviderCredentialsEditor = lazy(() => import('./views/ProviderCredentials/ProviderCredentialsEditor/ProviderCredentialsEditor'));
const IdentityProviderSettings = lazy(() => import('./views/IdentityProviderSettings/IdentityProviderSettings'));
const IdentityProviderSettingsEditor = lazy(() => import('./views/IdentityProviderSettings/IdentityProviderSettingsEditor/IdentityProviderSettingsEditor'));
const IdentitySettings = lazy(() => import('./views/IdentitySettings/IdentitySettings'));
const IdentitySettingsEditor = lazy(() => import('./views/IdentitySettings/IdentitySettingsEditor/IdentitySettingsEditor'));
const TicketTLO = lazy(() => import('./views/TicketTLO/TicketTLO'));
const TicketTLOEditor = lazy(() => import('./views/TicketTLO/TicketTLOEditor/TicketTLOEditor'));
const TicketTLOV2 = lazy(() => import('./views/TicketTLOV2/TicketTLOV2'));
const TicketTLOV2Editor = lazy(() => import('./views/TicketTLOV2/TicketTLOV2Editor'));
const UserPermissions = lazy(() => import('./views/UserPermissions/UserPermissions'));
const UserPermissionsEditor = lazy(() => import('./views/UserPermissions/UserPermissionsEditor/UserPermissionsEditor'));
const GlobalFiltersV2 = lazy(() => import('./views/GlobalFiltersV2/GlobalFiltersV2'));
const GlobalFiltersV2Editor = lazy(() => import('./views/GlobalFiltersV2/GlobalFiltersV2Editor'));
const ActivityLog = lazy(() => import('./views/ActivityLog/ActivityLog'));
const GeoGroupsEditor = lazy(() => import('./views/BusinessData/GeoGroups/GeoGroupsEditor'));
const GeoGroups = lazy(() => import('./views/BusinessData/GeoGroups/GeoGroups'));
const Supplier = lazy(() => import('./views/SupplierManager/Supplier'));
const SupplierEditor = lazy(() => import('./views/SupplierManager/SupplierEditor'));
const AncillariesMapping = lazy(() => import('./views/AncillariesMapping/AncillariesMapping'));
const AncillaryDistribution = lazy(() => import('./views/AncillaryDistribution/AncillaryDistribution'));
const AncillariesManager = lazy(() => import('./views/AncillariesManager/AncillariesManager'));
const AncillariesManagerEditor = lazy(() => import('./views/AncillariesManager/AncillariesManagerEditor'));
const AncillariesMappingEditor = lazy(() => import('./views/AncillariesMapping/AncillariesMappingEditor'));
const AncillaryDistributionEditor = lazy(() => import('./views/AncillaryDistribution/AncillaryDistributionEditor'));
const VccCredentials = lazy(() => import('./views/VccCredentials/VccCredentials'));
const MasterDataSetup = lazy(() => import('./views/MasterDataSetup/MasterDataSetup'));
function App() {
  return (
    <>
      <BrowserRouter>
        <ThemeProvider themeKey={ThemeConfigKeys.DefaultTheme}>
          <Provider store={applicationStore}>
            <AuthComponent>
              <Switch>
                <BaseLayout>
                  <Suspense fallback={<LoadingBlock />}>
                    <Switch>
                      <Route path="/" exact component={Login} />
                      <Route path={`/home`} exact component={Home} />
                      <PrivateRouter
                        category={CATEGORIES.SEARCHES_MANAGER}
                        path={`/${DEFAULT_PATHS.SEARCHES_MANAGER}/rule/:id`}
                        exact
                        component={SearchesManagerV2Editor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.SEARCHES_MANAGER}
                        path={`/${DEFAULT_PATHS.SEARCHES_MANAGER}/rule`}
                        exact
                        component={SearchesManagerV2Editor}
                      />
                      <PrivateRouter category={CATEGORIES.SEARCHES_MANAGER}
                        path={`/${DEFAULT_PATHS.SEARCHES_MANAGER}`}
                        exact component={SearchesManagerV2} />
                      <PrivateRouter
                        category={CATEGORIES.ADMIN_PORTAL}
                        path={`/${DEFAULT_PATHS.DIAGNOSTICS}`}
                        exact
                        component={Diagnostics}
                      />
                      <PrivateRouter
                        category={CATEGORIES.SHOPPING_DIAGNOSTICS}
                        path={`/${DEFAULT_PATHS.SHOPPING_DIAGNOSTICS}`}
                        exact
                        render={() => <Redirect to={`/${DEFAULT_PATHS.DIAGNOSTICS}`} />}
                      />
                      <PrivateRouter
                        category={CATEGORIES.SHOPPING_DIAGNOSTICS}
                        path={`/${DEFAULT_PATHS.SHOPPING_DIAGNOSTICS_V2}`}
                        exact
                        render={() => <Redirect to={`/${DEFAULT_PATHS.DIAGNOSTICS}`} />}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_REMARKS}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/remarks/rule/:id`}
                        exact
                        component={BookingCreateManagerV2RemarksEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_REMARKS}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/remarks/rule/`}
                        exact
                        component={BookingCreateManagerV2RemarksEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_REMARKS}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/remarks/`}
                        exact
                        render={(props) => {
                          const newProp = { ...props, activeTab: 'remarks' };
                          return <BookingCreateManagerV2 {...newProp} />;
                        }}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_SSR}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/ssr/`}
                        exact
                        render={(props) => {
                          const newProp = { ...props, activeTab: 'ssr' };
                          return <BookingCreateManagerV2 {...newProp} />;
                        }}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_SSR}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/ssr/rule/`}
                        exact
                        component={BookingCreateManagerV2SSREditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_SSR}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/ssr/rule/:id`}
                        exact
                        component={BookingCreateManagerV2SSREditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_OSIS}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/osi/rule/:id`}
                        exact
                        component={BookingCreateManagerV2Editor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_OSIS}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/osi/rule`}
                        exact
                        component={BookingCreateManagerV2Editor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_OSIS}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}/osi`}
                        exact
                        render={(props) => {
                          const newProp = { ...props, activeTab: 'osi' };
                          return <BookingCreateManagerV2 {...newProp} />;
                        }}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_CREATE_MANAGER_OSIS}
                        path={`/${DEFAULT_PATHS.BOOKING_CREATE_MANAGER}`}
                        exact
                        render={(props) => {
                          const newProp = { ...props, activeTab: 'osi' };
                          return <BookingCreateManagerV2 {...newProp} />;
                        }}
                      />
                      <PrivateRouter category={CATEGORIES.USER_PERMISSIONS} requireWriteAccess path={`/${DEFAULT_PATHS.USER_PERMISSIONS}`} exact component={UserPermissions} />
                      <PrivateRouter
                        category={CATEGORIES.USER_PERMISSIONS}
                        requireWriteAccess
                        path={`/${DEFAULT_PATHS.USER_PERMISSIONS}/edit/:id`}
                        exact
                        component={UserPermissionsEditor}
                      />
                      <PrivateRouter category={CATEGORIES.REGIONS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/regions/edit/:id`} exact component={RegionsEditor} />
                      <PrivateRouter category={CATEGORIES.REGIONS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/regions/new`} exact component={RegionsEditor} />
                      <Route path={`/${DEFAULT_PATHS.BUSINESS_DATA}`} exact component={BusinessData} />
                      <PrivateRouter category={CATEGORIES.REGIONS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/regions`} exact render={(props) => <BusinessData><Regions {...props} /></BusinessData>} />
                      <PrivateRouter
                        category={CATEGORIES.COUNTRIES}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/countries/edit/:id`}
                        exact
                        component={CountriesEditor}
                      />
                      <PrivateRouter category={CATEGORIES.COUNTRIES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/countries/new`} exact component={CountriesEditor} />
                      <PrivateRouter category={CATEGORIES.COUNTRIES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/countries`} exact render={(props) => <BusinessData><Countries {...props} /></BusinessData>} />
                      <PrivateRouter category={CATEGORIES.CITIES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/cities/edit/:id`} exact component={LocationsEditor} />
                      <PrivateRouter category={CATEGORIES.CITIES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/cities/new`} exact component={LocationsEditor} />
                      <PrivateRouter category={CATEGORIES.CITIES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/cities`} exact render={(props) => <BusinessData><Locations {...props} /></BusinessData>} />
                      <PrivateRouter
                        category={CATEGORIES.AIRPORTS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/stations/edit/:id`}
                        exact
                        component={StationsEditor}
                      />
                      <PrivateRouter category={CATEGORIES.AIRPORTS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/stations/new`} exact component={StationsEditor} />
                      <PrivateRouter category={CATEGORIES.AIRPORTS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/stations`} exact render={(props) => <BusinessData><Stations {...props} /></BusinessData>} />
                      <PrivateRouter
                        category={CATEGORIES.AIRLINES}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/airlines/edit/:id`}
                        exact
                        component={AirlinesEditor}
                      />
                      <PrivateRouter category={CATEGORIES.AIRLINES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/airlines/new`} exact component={AirlinesEditor} />
                      <PrivateRouter category={CATEGORIES.AIRLINES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/airlines`} exact render={(props) => <BusinessData><Airlines {...props} /></BusinessData>} />
                      <PrivateRouter
                        category={CATEGORIES.AGENCIES}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/agencies/edit/:id`}
                        exact
                        component={AgenciesEditor}
                      />
                      <PrivateRouter category={CATEGORIES.AGENCIES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/agencies/new`} exact component={AgenciesEditor} />
                      <PrivateRouter category={CATEGORIES.AGENCIES} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/agencies`} exact component={Agencies} />
                      <PrivateRouter
                        category={CATEGORIES.AGENCIES_GROUPS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/agency-groups/edit/:id`}
                        exact
                        component={AgencyGroupsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.AGENCIES_GROUPS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/agency-groups/new`}
                        exact
                        component={AgencyGroupsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.AGENCIES_GROUPS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/agency-groups`}
                        exact
                        component={AgencyGroups}
                      />                     
                      <PrivateRouter category={CATEGORIES.MARKUPS_V2} path={`/${DEFAULT_PATHS.MARKUPS}`} exact component={MarkupsV2} />
                      <PrivateRouter category={CATEGORIES.MARKUPS_V2} path={`/${DEFAULT_PATHS.MARKUPS}/rule`} exact component={MarkupsV2Editor} />
                      <PrivateRouter category={CATEGORIES.MARKUPS_V2} path={`/${DEFAULT_PATHS.MARKUPS}/rule/:id`} exact component={MarkupsV2Editor} />

                      <PrivateRouter
                        category={CATEGORIES.PROVIDER_PREFERENCE}
                        path={`/${DEFAULT_PATHS.PROVIDER_PREFERENCE}/new`}
                        exact
                        component={ProviderPreferenceEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.PROVIDER_PREFERENCE}
                        path={`/${DEFAULT_PATHS.PROVIDER_PREFERENCE}/edit/:id`}
                        exact
                        component={ProviderPreferenceEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.PROVIDER_PREFERENCE}
                        path={`/${DEFAULT_PATHS.PROVIDER_PREFERENCE}`}
                        exact
                        component={ProviderPreference}
                      />
                      <PrivateRouter
                        category={CATEGORIES.PROVIDER_CREDENTIALS}
                        path={`/${DEFAULT_PATHS.PROVIDER_CREDENTIALS}/new/`}
                        exact
                        component={ProviderCredentialsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.PROVIDER_CREDENTIALS}
                        path={`/${DEFAULT_PATHS.PROVIDER_CREDENTIALS}/edit/:id`}
                        exact
                        component={ProviderCredentialsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.PROVIDER_CREDENTIALS}
                        path={`/${DEFAULT_PATHS.PROVIDER_CREDENTIALS}`}
                        exact
                        component={ProviderCredentials}
                      />

                      <PrivateRouter category={CATEGORIES.TICKET_TLO} path={`/${DEFAULT_PATHS.TICKET_TLO}/rule/:id`} exact component={TicketTLOV2Editor} />
                      <PrivateRouter category={CATEGORIES.TICKET_TLO} path={`/${DEFAULT_PATHS.TICKET_TLO}/rule`} exact component={TicketTLOV2Editor} />
                      <PrivateRouter category={CATEGORIES.TICKET_TLO} path={`/${DEFAULT_PATHS.TICKET_TLO}`} exact component={TicketTLOV2} />

                      <PrivateRouter category={CATEGORIES.GLOBAL_FILTERS} path={`/${DEFAULT_PATHS.GLOBAL_FILTERS}/rule/:id`} exact component={GlobalFiltersV2Editor} />
                      <PrivateRouter category={CATEGORIES.GLOBAL_FILTERS} path={`/${DEFAULT_PATHS.GLOBAL_FILTERS}/rule`} exact component={GlobalFiltersV2Editor} />
                      <PrivateRouter category={CATEGORIES.GLOBAL_FILTERS} path={`/${DEFAULT_PATHS.GLOBAL_FILTERS}`} exact component={GlobalFiltersV2} />

                      <PrivateRouter category={CATEGORIES.BRANDS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/brands/edit/:id`} exact component={BrandsEditor} />
                      <PrivateRouter category={CATEGORIES.BRANDS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/brands/new`} exact component={BrandsEditor} />
                      <PrivateRouter category={CATEGORIES.BRANDS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/brands`} exact component={Brands} />
                      <PrivateRouter category={CATEGORIES.MARKETS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/markets/edit/:id`} exact component={MarketsEditor} />
                      <PrivateRouter category={CATEGORIES.MARKETS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/markets/new`} exact component={MarketsEditor} />
                      <PrivateRouter category={CATEGORIES.MARKETS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/markets`} exact component={Markets} />
                      <PrivateRouter
                        category={CATEGORIES.DISTRIBUTION_CHANNELS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/distribution-channels/edit/:id`}
                        exact
                        component={DistributionChannelsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.DISTRIBUTION_CHANNELS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/distribution-channels/new`}
                        exact
                        component={DistributionChannelsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.DISTRIBUTION_CHANNELS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/distribution-channels`}
                        exact
                        component={DistributionChannels}
                      />

                      <PrivateRouter
                        category={CATEGORIES.IDENTITY_PROVIDER_SETTINGS}
                        path={`/${DEFAULT_PATHS.IDENTITY_PROVIDER_SETTINGS}/edit/:id`}
                        exact
                        component={IdentityProviderSettingsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.IDENTITY_PROVIDER_SETTINGS}
                        path={`/${DEFAULT_PATHS.IDENTITY_PROVIDER_SETTINGS}/new`}
                        exact
                        component={IdentityProviderSettingsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.IDENTITY_PROVIDER_SETTINGS}
                        path={`/${DEFAULT_PATHS.IDENTITY_PROVIDER_SETTINGS}/`}
                        exact
                        component={IdentityProviderSettings}
                      />
                      <PrivateRouter category={CATEGORIES.ACTIVITY_LOG} path={`/${DEFAULT_PATHS.ACTIVITY_LOG}`} exact component={ActivityLog} />
                      <PrivateRouter
                        category={CATEGORIES.TECHNICAL_LOGS}
                        path={`/${DEFAULT_PATHS.TECHNICAL_LOGS}`}
                        exact
                        render={() => <Redirect to={`/${DEFAULT_PATHS.DIAGNOSTICS}?tab=technical-logs`} />}
                      />
                      <PrivateRouter
                        category={CATEGORIES.IDENTITY_SETTINGS}
                        path={`/${DEFAULT_PATHS.IDENTITY_SETTINGS}/edit/:id`}
                        exact
                        component={IdentitySettingsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.IDENTITY_SETTINGS}
                        path={`/${DEFAULT_PATHS.IDENTITY_SETTINGS}/new`}
                        exact
                        component={IdentitySettingsEditor}
                      />
                      <PrivateRouter category={CATEGORIES.IDENTITY_SETTINGS} path={`/${DEFAULT_PATHS.IDENTITY_SETTINGS}/`} exact component={IdentitySettings} />
                      <PrivateRouter
                        category={CATEGORIES.GEO_GROUPS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/geo-groups/edit/:id`}
                        exact
                        component={GeoGroupsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.GEO_GROUPS}
                        path={`/${DEFAULT_PATHS.BUSINESS_DATA}/geo-groups/new`}
                        exact
                        component={GeoGroupsEditor}
                      />
                      <PrivateRouter category={CATEGORIES.GEO_GROUPS} path={`/${DEFAULT_PATHS.BUSINESS_DATA}/geo-groups`} exact render={(props) => <BusinessData><GeoGroups {...props} /></BusinessData>} />
                      <PrivateRouter category={CATEGORIES.PROVIDERS} path={`/${DEFAULT_PATHS.PROVIDERS}`} exact component={Supplier} />
                      <PrivateRouter category={CATEGORIES.PROVIDERS} path={`/${DEFAULT_PATHS.PROVIDERS}/edit/:id`} exact component={SupplierEditor} />
                      <PrivateRouter category={CATEGORIES.PROVIDERS} path={`/${DEFAULT_PATHS.PROVIDERS}/new`} exact component={SupplierEditor} />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_MANAGER}
                        path={`/${DEFAULT_PATHS.ANCILLARY_MANAGER}`}
                        exact
                        component={AncillariesManager}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_MANAGER}
                        path={`/${DEFAULT_PATHS.ANCILLARY_MANAGER}/new`}
                        exact
                        component={AncillariesManagerEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_MANAGER}
                        path={`/${DEFAULT_PATHS.ANCILLARY_MANAGER}/edit/:id`}
                        exact
                        component={AncillariesManagerEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_SUPPLIER}
                        path={`/${DEFAULT_PATHS.ANCILLARY_SUPPLIER}`}
                        exact
                        component={AncillariesMapping}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_SUPPLIER}
                        path={`/${DEFAULT_PATHS.ANCILLARY_SUPPLIER}/edit/:id`}
                        exact
                        component={AncillariesMappingEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_SUPPLIER}
                        path={`/${DEFAULT_PATHS.ANCILLARY_SUPPLIER}/new`}
                        exact
                        component={AncillariesMappingEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_DISTRIBUTION}
                        path={`/${DEFAULT_PATHS.ANCILLARY_DISTRIBUTION}`}
                        exact
                        component={AncillaryDistribution}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_DISTRIBUTION}
                        path={`/${DEFAULT_PATHS.ANCILLARY_DISTRIBUTION}/edit/:id`}
                        exact
                        component={AncillaryDistributionEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.ANCILLARY_DISTRIBUTION}
                        path={`/${DEFAULT_PATHS.ANCILLARY_DISTRIBUTION}/new`}
                        exact
                        component={AncillaryDistributionEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.IDENTITY_PROVIDER_SETTINGS}
                        path={`/${DEFAULT_PATHS.MASTER_DATA_SETUP}/`}
                        exact
                        component={MasterDataSetup}
                      />
                      <PrivateRouter category={CATEGORIES.VCC_CREDENTIALS} path={`/${DEFAULT_PATHS.VCC_CREDENTIALS}`} exact component={VccCredentials} />

                      <PrivateRouter
                        category={CATEGORIES.VCC_CREDENTIALS}
                        path={`/${DEFAULT_PATHS.VCC_CREDENTIALS_WEX}/edit/:id`}
                        exact
                        component={WexCredentialsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.VCC_CREDENTIALS}
                        path={`/${DEFAULT_PATHS.VCC_CREDENTIALS_CONFERMA}/edit/:id`}
                        exact
                        component={VccCredentialsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.VCC_CREDENTIALS}
                        path={`/${DEFAULT_PATHS.VCC_CREDENTIALS_WEX}/new`}
                        exact
                        component={WexCredentialsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.VCC_CREDENTIALS}
                        path={`/${DEFAULT_PATHS.VCC_CREDENTIALS_CONFERMA}/new`}
                        exact
                        component={VccCredentialsEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.VCC_CREDENTIALS}
                        path={`/${DEFAULT_PATHS.VCC_CREDENTIALS}/new`}
                        exact
                        component={VccCredentialsEditor}
                      />
                     <Route path={`/${DEFAULT_PATHS.BOOKING_TOLERANCES}`} exact component={BookingTolerances} />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_TOLERANCES}
                        path={`/${DEFAULT_PATHS.BOOKING_TOLERANCES}/edit/:id`}
                        exact
                        component={BookingTolerancesEditor}
                      />
                      <PrivateRouter
                        category={CATEGORIES.BOOKING_TOLERANCES}
                        path={`/${DEFAULT_PATHS.BOOKING_TOLERANCES}/new`}
                        exact
                        component={BookingTolerancesEditor}
                      />
                      <Route path={`/403`} exact component={Page403} />
                      <Route component={Page404} />
                    </Switch>
                  </Suspense>
                </BaseLayout>
              </Switch>
            </AuthComponent>
          </Provider>
          <Provider store={notificationStore}>
            <NotificationsManager />
          </Provider>
        </ThemeProvider>
      </BrowserRouter>
    </>
  );
}

export default App;
